import React, { useState } from "react";
import useSWR from "swr";
import {
    Box,
    CircularProgress,
    Snackbar,
    Alert,
    Paper,
    Typography,
    TablePagination,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from "@mui/material";
import API_ENDPOINTS from "../../apis/endpoints";
import PerformanceChart from "../../components/PerformanceChart";
import SearchInput from "../../components/SearchInput";

const GamesDashboard = () => {
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sortColumn, setSortColumn] = useState("player_name");
    const [sortDirection, setSortDirection] = useState("asc");
    const [selectedMetric, setSelectedMetric] = useState("goals");

    const queryParam = searchQuery ? `&query=${searchQuery}` : "";
    const sortParam = `&sort=${
        sortDirection === "desc" ? `-${sortColumn}` : sortColumn
    }`;

    const { data, error } = useSWR(
        `${API_ENDPOINTS.FOOTBALL}?limit=${rowsPerPage}&offset=${
            page * rowsPerPage
        }${queryParam}${sortParam}`
    );

    const handleSearch = (term) => {
        setSearchQuery(term);
        setPage(0);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSortRequest = (column) => {
        const isAsc = sortColumn === column && sortDirection === "asc";
        setSortDirection(isAsc ? "desc" : "asc");
        setSortColumn(column);
    };

    const handleMetricChange = (event) => {
        setSelectedMetric(event.target.value);
    };

    return (
        <Box className="dashboard-container">
            <Typography variant="h4" gutterBottom>
                Football Dashboard
            </Typography>
            <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                mb={2}
            >
                <SearchInput
                    onSearch={handleSearch}
                    placeHolder={"Games"}
                    searchTerm={searchQuery}
                    setSearchTerm={setSearchQuery}
                />
            </Box>

            {error ? (
                <Snackbar open autoHideDuration={6000}>
                    <Alert severity="error">
                        An error occurred. Please try again later.
                    </Alert>
                </Snackbar>
            ) : (
                <>
                    <Paper className="table-box table-paper">
                        {!data ? (
                            <Box className="loader-container">
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                <TableContainer className="table-container">
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell className="table-header-cell">
                                                    <TableSortLabel
                                                        active={
                                                            sortColumn ===
                                                            "player_name"
                                                        }
                                                        direction={
                                                            sortColumn ===
                                                            "player_name"
                                                                ? sortDirection
                                                                : "asc"
                                                        }
                                                        onClick={() =>
                                                            handleSortRequest(
                                                                "player_name"
                                                            )
                                                        }
                                                    >
                                                        Player Name
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell className="table-header-cell">
                                                    Club Name
                                                </TableCell>
                                                <TableCell className="table-header-cell">
                                                    Date
                                                </TableCell>
                                                <TableCell className="table-header-cell">
                                                    Games
                                                </TableCell>
                                                <TableCell className="table-header-cell">
                                                    Goals
                                                </TableCell>
                                                <TableCell className="table-header-cell">
                                                    Assists
                                                </TableCell>
                                                <TableCell className="table-header-cell">
                                                    Chances Created
                                                </TableCell>
                                                <TableCell className="table-header-cell">
                                                    Successful Passes
                                                </TableCell>
                                                <TableCell className="table-header-cell">
                                                    Touches
                                                </TableCell>
                                                <TableCell className="table-header-cell">
                                                    Interceptions
                                                </TableCell>
                                                <TableCell className="table-header-cell">
                                                    Clean Sheets
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.results
                                                .slice(0, rowsPerPage)
                                                .map((player) => (
                                                    <TableRow
                                                        key={player.id}
                                                        className="table-row"
                                                    >
                                                        <TableCell className="table-cell">
                                                            {player.player_name}
                                                        </TableCell>
                                                        <TableCell className="table-cell">
                                                            {player.club_name}
                                                        </TableCell>
                                                        <TableCell className="table-cell">
                                                            {player.date}
                                                        </TableCell>
                                                        <TableCell className="table-cell">
                                                            {player.games}
                                                        </TableCell>
                                                        <TableCell className="table-cell">
                                                            {player.goals}
                                                        </TableCell>
                                                        <TableCell className="table-cell">
                                                            {player.assists}
                                                        </TableCell>
                                                        <TableCell className="table-cell">
                                                            {
                                                                player.chances_created
                                                            }
                                                        </TableCell>
                                                        <TableCell className="table-cell">
                                                            {
                                                                player.successful_passes
                                                            }
                                                        </TableCell>
                                                        <TableCell className="table-cell">
                                                            {player.touches}
                                                        </TableCell>
                                                        <TableCell className="table-cell">
                                                            {
                                                                player.interceptions
                                                            }
                                                        </TableCell>
                                                        <TableCell className="table-cell">
                                                            {
                                                                player.clean_sheets
                                                            }
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <TablePagination
                                    component="div"
                                    count={data.meta.total}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={
                                        handleChangeRowsPerPage
                                    }
                                />
                            </>
                        )}
                    </Paper>

                    <Box mt={3}>
                        <Typography variant="h6">
                            Select Metric for Chart:
                        </Typography>
                        <select
                            value={selectedMetric}
                            onChange={handleMetricChange}
                        >
                            {/* Dropdown options for metrics */}
                            <option value="goals">Goals</option>
                            <option value="successful_passes">
                                Successful Passes
                            </option>
                            <option value="chances_created">
                                Chances Created
                            </option>
                            <option value="assists">Assists</option>
                            <option value="touches">Touches</option>
                        </select>
                    </Box>

                    {/* Chart displaying selected metric */}
                    <Paper
                        sx={{
                            width: "100%",
                            overflow: "hidden",
                            marginTop: "20px",
                        }}
                        className="chart-box"
                    >
                        {!data ? (
                            // Loader for chart
                            <Box className="loader-container">
                                <CircularProgress />
                            </Box>
                        ) : (
                            // Chart component with selected metric
                            <PerformanceChart
                                data={data?.results.slice(0, rowsPerPage)}
                                metric={selectedMetric}
                            />
                        )}
                    </Paper>
                </>
            )}
        </Box>
    );
};

export default function MonitoredGamesDashboard() {
    return (
        <ErrorBoundary>
            <GamesDashboard />
        </ErrorBoundary>
    );
}

// Error boundary for monitoring component errors
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error("ErrorBoundary caught an error:", error, errorInfo);
        // Optionally log to an external service like Sentry
    }

    render() {
        if (this.state.hasError) {
            return <h1>Something went wrong.</h1>;
        }

        return this.props.children;
    }
}
